import { cn } from '@/lib/utils';
import { AlertTriangle, Info } from 'lucide-react';
import { createPortal } from 'react-dom';
import { LoadingSpinner } from './loading-spinner';

interface ConfirmDialogProps {
  /** Whether the dialog is visible */
  open: boolean;
  /** Dialog title */
  title: string;
  /** Description / body message */
  message: string;
  /** Label for the confirm button */
  confirmLabel?: string;
  /** Label for the cancel button */
  cancelLabel?: string;
  /** Danger mode — red confirm button + warning icon */
  danger?: boolean;
  /** Disable buttons while an action is pending */
  isPending?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Reusable confirmation dialog overlay.
 *
 * ```tsx
 * <ConfirmDialog
 *   open={showDialog}
 *   title="Delete user?"
 *   message="This cannot be undone."
 *   confirmLabel="Delete"
 *   danger
 *   onConfirm={handleDelete}
 *   onCancel={() => setShowDialog(false)}
 * />
 * ```
 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  danger = false,
  isPending = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  if (!open) return null;
  // SSR guard — `document` is undefined during server rendering. We only ever
  // render this in a browser, but `createPortal` would still throw at module
  // analysis time without the check.
  if (typeof document === 'undefined') return null;

  const IconComponent = danger ? AlertTriangle : Info;
  const iconWrapperClass = danger
    ? 'bg-red-100 dark:bg-red-900/30'
    : 'bg-blue-100 dark:bg-blue-900/30';
  const iconClass = danger ? 'text-red-600 dark:text-red-400' : 'text-blue-600 dark:text-blue-400';

  // Render via Portal at document.body so the dialog escapes any nested
  // stacking context / overflow:hidden ancestor in the caller's tree (e.g.
  // dropdown menus, scrollable panels). z-[10000] keeps it above the
  // ghost-session banner (z-[9999]) so SUPER_ADMINs impersonating other
  // users can still confirm destructive actions.
  return createPortal(
    <>
      <div className="fixed inset-0 z-[10000] bg-black/50" onClick={onCancel} />
      <div className="fixed inset-0 z-[10001] flex items-center justify-center p-4">
        <div
          className="bg-card w-full max-w-sm rounded-xl border shadow-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6">
            <div className="mb-4 flex items-center gap-3">
              <div
                className={cn(
                  'flex h-10 w-10 items-center justify-center rounded-full',
                  iconWrapperClass
                )}
              >
                <IconComponent className={cn('h-5 w-5', iconClass)} />
              </div>
              <h2 className="text-lg font-semibold">{title}</h2>
            </div>
            <p className="text-muted-foreground mb-6 text-sm">{message}</p>
            <div className="flex gap-3">
              <button
                onClick={onCancel}
                disabled={isPending}
                className="hover:bg-muted flex-1 rounded-lg border px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50"
              >
                {cancelLabel}
              </button>
              <button
                onClick={onConfirm}
                disabled={isPending}
                className={cn(
                  'flex flex-1 items-center justify-center rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-50',
                  danger ? 'bg-red-600 hover:bg-red-700' : 'bg-primary hover:bg-primary/90'
                )}
              >
                {isPending ? <LoadingSpinner size="sm" variant="icon" /> : confirmLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}
